import Jimp from 'jimp';
import {ColorAction} from '@jimp/plugin-color'


type Lever = {
  "token-id": number,
  "lever-id": number
};

type TimeValue = {
  type: "DAY_OF_YEAR"|"HOUR_OF_DAY",
  handler: {
    type: "MODULO"|"CUSTOM",
    // [minInclusive, maxInclusive, index]
    rules: [number, number, number][],
    max_bound_inclusive: number
  }
}

type DynamicValue<T> = T|Lever|TimeValue;
type DynamicNumber = DynamicValue<number>;

// This is distinct. It has multiple sub-layer definitions, and one can be active.
type StatesLayer = {
  states: {
    options: ({label: string} & LayerDefinition)[],
  } & Lever
}

// A normal layer with a number of properties which can be combined
type DefaultLayer = {
  uri: string,
  width?: number,
  height?: number,
  visible?: DynamicNumber,
  "fixed-position"?: {x: DynamicNumber, y: DynamicNumber},
  "relative-position"?: {x: DynamicNumber, y: DynamicNumber},
  "fixed-rotation"?: DynamicNumber,
  "orbit-rotation"?: DynamicNumber,
  scale?: {x: DynamicNumber, y: DynamicNumber},
  mirror?: {x: DynamicNumber, y: DynamicNumber},
  anchor?: string|({
    options: string[],
  } & Lever),
  color?: {
    alpha?: DynamicNumber,
    red?: DynamicNumber,
    blue?: DynamicNumber,
    green?: DynamicNumber,
    hue?: DynamicNumber,
    brightness?: DynamicNumber,
    saturation?: DynamicNumber,

    opacity?: DynamicNumber,
    hardlight?: DynamicNumber,
    multiply?: DynamicNumber,
    lighten?: DynamicNumber,
    overlay?: DynamicNumber,
    difference?: DynamicNumber,
    exclusion?: DynamicNumber,
    screen?: DynamicNumber,
  }
}

type LayerDefinition = StatesLayer|DefaultLayer;

export type Layout = {
  type: 'layered_static',
  version: 1,
  layers: ({id: string} & LayerDefinition)[]
}

export type Manifest = {
  name: string,
  description: string,
  image: string,
  attributes: {trait_type: string, value: string}[]
  layout: Layout
}

type ControlTokenAPI = {
  getLeverValue(tokenId: number, leverId: number): number,
  getTimestamp(): number
}

export type Connector = {
  loadFromURI(hash: string): Promise<Buffer>;
}

type LayerRuntimeData = {
  // Based on the center of the layer
  position?: {x: number, y: number},
  active?: string
}
// How one layer renders can effect other layers. Here we track the state of each layer.
type LayerRuntimeState = {[layerId: string]: LayerRuntimeData|undefined};


type Scale = {type: 'scale', x: number, y: number};
type Rotate = {type: 'rotate', deg: number};
type Mirror = {type: 'mirror', horz: boolean, vert: boolean};
type Channel = {type: 'channel', red?: number, blue?: number, green?: number};
type HSV = {type: 'hsv', hue?: number, brightness?: number, saturation?: number};
type Opacity = {type: 'opacity', alpha: number};
type Transform = Scale|Rotate|Mirror|Channel|HSV|Opacity;


/**
 * Defines how each layer is supposed to be rendered.
 */
type CompositeOptions = {
  x: number,
  y: number,
  mode?: string,
  opacitySource?: number,
  transforms?: Transform[]
};

function isLever(value: DynamicValue<any>): value is Lever {
  return typeof value === 'object' && 'token-id' in value;
}

function isTimeValue(value: DynamicValue<any>): value is TimeValue {
  return typeof value === 'object' && 'token-id' in value;
}

function isStatesLayer(layer: LayerDefinition): layer is StatesLayer {
  return 'states' in layer;
}

export type ArtworkLevers = {[layerId: number]: {[leverId: number]: any}};
export type ArtworkState = {[leverId: string]: any};

export type LayerCache = {[uri: string]: Jimp};


async function getLayerWithCache(cache: LayerCache, connector: Connector, uri: string): Promise<Jimp> {
  if (!cache[uri]) {
    const buffer = await connector.loadFromURI(uri);
    cache[uri] = await Jimp.read(buffer);
  }

  return cache[uri]!;
}

// Each layer can have a token.
// Each token can have multiple levers.
export function getLeverValue(state: ArtworkState, tokenId: number, leverId: number) {
  const key = `${tokenId}/${leverId}`;
  if (state?.[key] !== undefined) {
    return state?.[key];
  }
  throw new Error(`State is incomplete, missing ${key}, known=${Object.keys(state).join(", ")}`)
}


/**
 * Renders an artwork and keeps a cache of individual layers around.
 */
export class Artwork {
  private layout: Layout;
  private masterTokenId: number;
  private connector: Connector;
  private state: ArtworkState = {};
  private cache: LayerCache = {};

  constructor(layout: Layout, masterTokenId: number, opts: {
    connector: Connector
  }) {
    this.layout = layout;
    this.masterTokenId = masterTokenId;
    this.connector = opts.connector;
  }

  // States layer can have nested states layer or other kinds, so resolve those first
  private resolveStatesLayer(layer: LayerDefinition&{id: string}): ({id: string} & DefaultLayer)|undefined {
    const id = layer.id;
    let layerDef = layer as LayerDefinition;

    while (isStatesLayer(layerDef)) {
      const uriIndex = resolveDynamicValue(layerDef.states, "Layer Index", this.api, this.masterTokenId);
      if (!layerDef.states.options[uriIndex]) {
        console.log('ERROR resolving state layer: ', layerDef, uriIndex);
        return;
      }
      layerDef = layerDef.states.options[uriIndex];
    }
    return {...layerDef, id};
  }

  private isLayerVisible(layerDef: DefaultLayer) {
    if (layerDef.visible !== undefined) {
      const isVisible = (resolveDynamicValue(layerDef.visible, "Layer Visible", this.api, this.masterTokenId)) === 1;
      if (!isVisible) {
        return false;
      }
    }

    return true;
  }

  private get api() {
    return {
      getLeverValue: (layerId: number, leverId: number) => getLeverValue(this.state, layerId, leverId),
      getTimestamp(): number {
        return Math.round(Date.now() / 1000);
      }
    }
  }

  async setState(state: ArtworkState): Promise<Jimp> {
    this.state = state;

    // So called "state" layers select between multiple possible states, each being a layer of its own.
    // Resolve those to the sublayer we should paint.
    const resolvedStateLayers = this.layout.layers.map(layer => this.resolveStatesLayer(layer)!).filter(l => !!l);

    // Some layers are not visible. Ignore those completely.
    const visibleLayers = resolvedStateLayers.filter(layer => this.isLayerVisible(layer));

    console.log('Layer State to be rendered: ', visibleLayers)

    // Load all image urls that we need to draw
    console.log("Load raw images...")
    const loadingPromises: (Promise<Jimp>|null)[] = visibleLayers.map(
        layerDef => layerDef.uri ? getLayerWithCache(this.cache, this.connector, layerDef.uri) : null);
    const rawJimpImages = await Promise.all(loadingPromises);


    const instructions = getRenderInstructions(visibleLayers, rawJimpImages, this.api, this.masterTokenId)

    console.log("Render each layer...")
    const finalImage = await renderJimp(visibleLayers, rawJimpImages, instructions);
    return finalImage;
  }
}

function resolveDynamicValue(value: DynamicValue<number>, label: string, tokens: ControlTokenAPI, masterTokenId: number): number {
  if (isLever(value)) {
    // layer token ids are relative to their master token id
    const tokenId = masterTokenId + value["token-id"];
    const leverId = value["lever-id"];
    return tokens.getLeverValue(tokenId, leverId);
  }

  else if (isTimeValue(value)) {
    const timestamp = tokens.getTimestamp();
    const date = new Date(timestamp * 1000);

    let measureValue = 0;

    if (value.type === "DAY_OF_YEAR") {
      // create a date at the start of this year
      const start = new Date(Date.UTC(date.getUTCFullYear(), 0, 0));
      // get diff in milliseconds
      const diff = ((date as any) - (start as any)) + ((start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000);
      const oneDay = 1000 * 60 * 60 * 24;
      // divide diff by 1 day of milliseconds
      measureValue = Math.floor(diff / oneDay);
    }
    else if (value.type === "HOUR_OF_DAY") {
      measureValue = date.getUTCHours();
    }

    // Determine the handler type for time
    if (value.handler.type === "MODULO") {
      // get the max bound inclusive
      const maxBoundInclusive = value.handler.max_bound_inclusive;
      // modulo to keep the value within the max bound inclusive
      return measureValue % (maxBoundInclusive + 1);
    }

    else if (value.handler.type === "CUSTOM") {
      let v = 0; // value defaults at 0 if we can't find a rule that applies
      const customRules = value.handler.rules;
      // find the first rule that applies and stop
      for (let i = 0; i < customRules.length; i++) {
        // format of a rule is: [minInclusive, maxInclusive, index]
        if ((measureValue >= customRules[i][0]) && (measureValue <= customRules[i][1])) {
          v = customRules[i][2];
          break;
        }
      }

      return v;
    }

    else {
      throw new Error("Not a valid handler: " + value.handler.type)
    }
  }

  else {
    return value;
  }
}

function getLayerRuntimeMeta(runtime: LayerRuntimeState, layerId: string): LayerRuntimeData|null {
  const entry = runtime[layerId];
  if (!entry) {
    return null;
  }

  if (entry.active) {
    return getLayerRuntimeMeta(runtime, entry.active);
  }

  return entry;
}


// TODO: Build a canvas renderer: https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/globalCompositeOperation
// https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
async function renderJimp(layers: ({id: string} & DefaultLayer)[], rawImages: (Jimp|null)[], instructions: (null|CompositeOptions)[]) {
  // Pre-render them all as well, whenever the state has changed.
  // TODO: We currently are only able to *load* the image data in parallel, but we cannot easily render
  // in parallel as layers can reference each other. async itself currently requires that the layers be
  // ordered correctly for this and only reference earlier layers, so looping works without doing a proper
  // dependency resolver.
  const renderedLayers: Jimp[] = [];

  console.log(instructions);

  for (let i=0; i<layers.length; i++) {
    const layerDef = layers[i];
    // XXX: if not changed, continue

    // If this image has no uri, it will be the empty background.
    if (layerDef.uri === undefined) {
      const layerImage = await new Jimp(layerDef.width, layerDef.height);
      renderedLayers.push(layerImage);
    }
    else {
      await renderLayerWithJimp(instructions[i]?.transforms ?? [], layerDef, rawImages[i]!);
      renderedLayers.push(rawImages[i]!);
    }
  }

  // Finally, render all together
  console.log("Compositing...")
  let finalImage = new Jimp(renderedLayers[0]!.bitmap.width, renderedLayers[0]!.bitmap.height);

  // Assumes all images have been loaded.
  renderedLayers.forEach((renderedLayer, idx) => {
    // An layer can render to null in certain cases, such as when scale = 0.
    if (renderedLayer === null) {
      return;
    }

    const comp = instructions[idx]!;

    // offset x and y so that layers are drawn at the center of their image
    const x = comp?.x - (renderedLayers[idx].bitmap.width / 2);
    const y = comp?.y - (renderedLayers[idx].bitmap.height / 2);

    finalImage.composite(renderedLayer, x, y, comp?.mode ? {
      mode: comp.mode!,
      opacitySource: comp.opacitySource!,
    } as any : undefined);
  });

  return finalImage;
}


async function renderLayerWithJimp(transforms: Transform[], layer: DefaultLayer, layerImage: Jimp) {
  for (const transform of transforms) {
    if (transform.type === 'scale') {
      let bitmapWidth = layerImage.bitmap.width * transform.x;
      let bitmapHeight = layerImage.bitmap.height * transform.y;
      layerImage.resize(bitmapWidth, bitmapHeight);
    }
    else if (transform.type === 'rotate') {
      layerImage.rotate(transform.deg, true);
    }
    else if (transform.type === 'mirror') {
      layerImage.mirror(transform.horz, transform.vert);
    }
    else if (transform.type === 'hsv') {
      if (transform.brightness) {
        layerImage.brightness(transform.brightness);
      }
    }
    if (transform.type === 'opacity') {
      layerImage.opacity(transform.alpha);
    }
  }

  // color transforms we can apply in one step
  const colorTransforms: ColorAction[] = [];
  for (const transform of transforms) {
    if (transform.type === 'channel') {
      if (transform.red !== undefined) {
        colorTransforms.push({apply: 'red', params: [transform.red]});
      }
      if (transform.blue !== undefined) {
        colorTransforms.push({apply: 'blue', params: [transform.blue]});
      }
      if (transform.green !== undefined) {
        colorTransforms.push({apply: 'green', params: [transform.green]});
      }
    }

    if (transform.type === 'hsv') {
      if (transform.hue !== undefined) {
        colorTransforms.push({apply: 'hue', params: [transform.hue]});
      }
      if (transform.saturation !== undefined) {
        // @ts-ignore: original async-art script used this, maybe the type definitions are just missing it?
        colorTransforms.push({apply: 'saturate', params: [transform.saturation]});
      }
    }
  }
  if (colorTransforms.length) {
    layerImage.color(colorTransforms);
  }
}


/**
 * Get render instructions for a set of layers.
 */
function getRenderInstructions(layers: ({id: string} & DefaultLayer)[], rawImages: (Jimp|null)[], api: ControlTokenAPI, masterTokenId: number) {
  const runtimeLayerDb: LayerRuntimeState = {};

  const layerCompositeOptions: (CompositeOptions|null)[] = [];
  for (let i=0; i<layers.length; i++) {
    const layerDef = layers[i];
    // If this image has no uri, it will be the empty background.
    if (layerDef.uri === undefined) {
      runtimeLayerDb[layerDef.id] = {position: {x: layerDef.width! / 2, y: layerDef.height! / 2}};
      layerCompositeOptions.push(null);
    }
    else {
      const [instructions, meta] = getLayerRenderInstructions(runtimeLayerDb, layerDef, rawImages[i], api, masterTokenId);
      runtimeLayerDb[layerDef.id] = meta ?? undefined;
      layerCompositeOptions.push(instructions);
    }
  }
  return layerCompositeOptions;
}

/**
 * Render `layerImage` (with the properties defined in `layer`) onto `image`.
 */
function getLayerRenderInstructions(
  runtime: LayerRuntimeState,
  layer: DefaultLayer,
  image: Jimp|null,
  tokens: ControlTokenAPI,
  tokenId: number
): [CompositeOptions|null, LayerRuntimeData|null] {

  // The width/height of that layer - there are two options. Either the layer gave a uri, in which case
  // we downloaded the image and know the size - or the layer is empty, and needs to specify width/height
  let width, height;
  if (image) {
    width = image.bitmap.width;
    height = image.bitmap.height;
  } else {
    width = layer.width!;
    height = layer.height!;
  }

  const transforms: Transform[] = [];
  let compositeMode: string|undefined = undefined;
  let opacitySource: number|undefined = undefined;

  if (layer.scale) {
    const scaleX = resolveDynamicValue(layer.scale.x, "Layer Scale X", tokens, tokenId) / 100;
    const scaleY = resolveDynamicValue(layer.scale.y, "Layer Scale Y", tokens, tokenId) / 100;
    if ((scaleX === 0) || (scaleY === 0)) {
      console.log("scale X or Y is 0 -- this layer is invisible.")
      return [null, null];
    }
    transforms.push({type: 'scale', x: scaleX, y: scaleY});
  }

  if (layer["fixed-rotation"] !== undefined) {
    let rotation = resolveDynamicValue(layer["fixed-rotation"], "Layer Fixed Rotation", tokens, tokenId);
    // XXX: They are fitting extra data in there
    // if (KEY_MULTIPLIER in layer["fixed-rotation"]) {
    //   const multiplier = resolveDynamicValue(layer["fixed-rotation"][KEY_MULTIPLIER], "Rotation Multiplier", tokens, tokenId);
    //   rotation *= multiplier;
    // }
    transforms.push({type: 'rotate', deg: rotation});
  }

  if (layer.mirror) {
    const shouldMirrorHorizontal = ((resolveDynamicValue(layer.mirror.x, "Mirror X", tokens, tokenId)) === 1);
    const shouldMirrorVertical = ((resolveDynamicValue(layer.mirror.y, "Mirror Y", tokens, tokenId)) === 1);
    transforms.push({type: 'mirror', horz: shouldMirrorHorizontal, vert: shouldMirrorVertical});
  }

  if (layer.color) {
    const {color} = layer;

    const blue = color.blue ? resolveDynamicValue(color.blue, "Layer Color Blue", tokens, tokenId) : undefined;
    const green = color.green ? resolveDynamicValue(color.green, "Layer Color Green", tokens, tokenId) : undefined;
    const red = color.red ? resolveDynamicValue(color.red, "Layer Color Red", tokens, tokenId) : undefined;
    if (blue || green || red) {
      transforms.push({
        type: 'channel',
        red: red === 0 ? undefined : red,
        blue: blue === 0 ? undefined : blue,
        green: green === 0 ? undefined : green
      })
    }

    const hue = color.hue ? resolveDynamicValue(color.hue, "Layer Color Hue", tokens, tokenId) : undefined;
    const brightness = color.brightness ? resolveDynamicValue(color.brightness, "Layer Color Brightness", tokens, tokenId) : undefined;
    const saturation = color.saturation ? resolveDynamicValue(color.saturation, "Layer Color Saturation", tokens, tokenId) : undefined;
    if (hue || brightness || saturation) {
      transforms.push({
        type: 'hsv',
        hue: hue === 0 ? undefined : hue,
        brightness: brightness === 0 || brightness === undefined ? undefined : brightness / 100,
        saturation: saturation === 0 ? undefined : saturation
      })
    }

    if (color.alpha) {
      const alpha = resolveDynamicValue(color.alpha, "Layer Color Alpha", tokens, tokenId);
      transforms.push({
        type: 'opacity',
        alpha: alpha/100
      })
    }

    if (layer.color.multiply) {
      const shouldMultiply = ((resolveDynamicValue(layer.color.multiply, "Layer Color Should Multiply", tokens, tokenId)) > 0);

      if (shouldMultiply) {
        compositeMode = Jimp.BLEND_MULTIPLY;
        if (layer.color.opacity) {
          const opacity = resolveDynamicValue(layer.color.opacity!, "Layer Multiply Opacity", tokens, tokenId);
          opacitySource = opacity / 100.0;
        }
      }
    }

    if (layer.color.hardlight) {
      const shouldHardlight = ((resolveDynamicValue(layer.color.hardlight, "Layer Color Should Hardlight", tokens, tokenId)) > 0);

      if (shouldHardlight) {
        compositeMode = Jimp.BLEND_HARDLIGHT;
        if (layer.color.opacity) {
          const opacity = resolveDynamicValue(layer.color.opacity!, "Layer Hardlight Opacity", tokens, tokenId);
          opacitySource = opacity / 100.0;
        }
      }
    }

    if (layer.color.lighten) {
      const shouldLighten = ((resolveDynamicValue(layer.color.lighten, "Layer Color Should Lighten", tokens, tokenId)) > 0);

      if (shouldLighten) {
        compositeMode = Jimp.BLEND_LIGHTEN;

        if (layer.color.opacity) {
          const opacity = resolveDynamicValue(layer.color.opacity!, "Layer Lighten Opacity", tokens, tokenId);
          opacitySource = opacity / 100.0;
        }
      }
    }

    if (layer.color.overlay) {
      const shouldOverlay = ((resolveDynamicValue(layer.color.overlay, "Layer Color Should Overlay", tokens, tokenId)) > 0);

      if (shouldOverlay) {
        compositeMode = Jimp.BLEND_OVERLAY;

        if (layer.color.opacity) {
          const opacity = resolveDynamicValue(layer.color.opacity!, "Layer Overlay Opacity", tokens, tokenId);
          opacitySource = opacity / 100.0;
        }
      }
    }

    if (layer.color.difference) {
      const shouldDifference = ((resolveDynamicValue(layer.color.difference, "Layer Color Should Difference", tokens, tokenId)) > 0);

      if (shouldDifference) {
        compositeMode = Jimp.BLEND_DIFFERENCE;

        if (layer.color.opacity) {
          const opacity = resolveDynamicValue(layer.color.opacity!, "Layer Difference Opacity", tokens, tokenId);
          opacitySource = opacity / 100.0;
        }
      }
    }

    if (layer.color.exclusion) {
      const shouldExclusion = ((resolveDynamicValue(layer.color.exclusion, "Layer Color Should Exclusion", tokens, tokenId)) > 0);

      if (shouldExclusion) {
        compositeMode = Jimp.BLEND_EXCLUSION;

        if (layer.color.opacity) {
          const opacity = resolveDynamicValue(layer.color.opacity!, "Layer Exclusion Opacity", tokens, tokenId);
          opacitySource = opacity / 100.0;
        }
      }
    }

    if (layer.color.screen) {
      const shouldScreen = ((resolveDynamicValue(layer.color.screen, "Layer Color Should Screen", tokens, tokenId)) > 0);

      if (shouldScreen) {
        compositeMode = Jimp.BLEND_SCREEN;

        if (layer.color.opacity) {
          const opacity = resolveDynamicValue(layer.color.opacity!, "Layer Screen Opacity", tokens, tokenId);
          opacitySource = opacity / 100.0;
        }
      }
    }
  }

  /**
   * figure out the position.
   */

  // our coordinates are center-of-layer based, by default we assume all layers have the same size and are within
  // their own center (TODO: Probably wrong, should be within the center of the first layer).
  let x: number = width / 2;
  let y: number = height / 2;

  if (layer.anchor) {
    let anchorLayerId: string;
    if (typeof layer.anchor === "object") {
      const anchorLayerIndex = resolveDynamicValue(layer.anchor, "Anchor Layer Index", tokens, tokenId);
      anchorLayerId = layer.anchor.options[anchorLayerIndex];
    } else {
      anchorLayerId = layer.anchor;
    }

    const anchorMeta = getLayerRuntimeMeta(runtime, anchorLayerId)!;
    x = anchorMeta.position!.x;
    y = anchorMeta.position!.y;
  }
  else if (layer["fixed-position"]) {
    // Fixed position sets an absolute position
    x = resolveDynamicValue(layer["fixed-position"].x, "Layer Fixed Position X", tokens, tokenId);
    y = resolveDynamicValue(layer["fixed-position"].y, "Layer Fixed Position Y", tokens, tokenId);
  }

  // We can also be relative to the anchor
  let relativeX = 0;
  let relativeY = 0;

  // relative position adjusts xy based on the anchor
  if (layer["relative-position"]) {
    relativeX = resolveDynamicValue(layer["relative-position"].x, "Layer Relative Position X", tokens, tokenId);
    relativeY = resolveDynamicValue(layer["relative-position"].y, "Layer Relative Position Y", tokens, tokenId);
  }

  // relative rotation orbits this layer around an anchor
  if (layer["orbit-rotation"]) {
    const relativeRotation = resolveDynamicValue(layer["orbit-rotation"], "Layer Orbit Rotation", tokens, tokenId);
    const rad = -relativeRotation * Math.PI / 180;

    relativeX = Math.round(relativeX * Math.cos(rad) - relativeY * Math.sin(rad));
    relativeY = Math.round(relativeY * Math.cos(rad) + relativeX * Math.sin(rad));
  }

  x += relativeX;
  y += relativeY;


  // stamp the final center X and Y that this layer was rendered at (for any follow-up layers that might be anchored here)
  // set active to be true so that any subsequent layers that are anchored to this can tell which layer was active (for multi state layers)
  const renderData: LayerRuntimeData = {
    position: {x, y},
  }

  return [{x, y, transforms, mode: compositeMode, opacitySource}, renderData];
}


// All key paths within a `LayerDefinition` which can have control levers.
const LeverKeys = [
  "visible",
  "fixed-position.x",
  "fixed-position.y",
  "relative-position.x",
  "relative-position.y",
  "scale.x",
  "scale.y",
  "mirror.x",
  "mirror.y",
  "fixed-rotation",
  "orbit-rotation",
  "anchor",
  "color.alpha",
  "color.red",
  "color.blue",
  "color.green",
  "color.hue",
  "color.brightness",
  "color.saturation",

  "color.opacity",
  "color.hardlight",
  "color.multiply",
  "color.lighten",
  "color.overlay",
  "color.difference",
  "color.exclusion",
  "color.screen",
];

// Drawn from the layer definition via `indexAllLevers`. Gives us info about levers with are otherwise anonymous
// numbers, such as which layers they effect.
export type LeverDefinition = {
  leverId: string,
  // parent layers to reach this lever
  location: string[],
  // parent levers have to be set to which values for this to be used
  restrictions: {[leverId: string]: any},
  // Which property is effected
  type: string,
  // Only for type=states: The label of each sublayer option.
  optionLabels?: string[]
}

/**
 * Given a "layout" structure (which is a list of layers with nesting), we find all those layers which
 * do have a control lever. We then return a list of those levers, which information about which layers
 * and which properties they modify.
 */
export function indexAllLevers(layout: Layout): LeverDefinition[] {
  const getLeverId = (lever: Lever) => { return `${lever["token-id"]}/${lever["lever-id"]}` }

  function *indexLayer(layer: LayerDefinition, name: string, location: string[], restrictions: {[leverId: string]: any}): Iterable<LeverDefinition> {
    if (isStatesLayer(layer)) {
      const leverId = getLeverId(layer.states);
      const newLocation = [...location, name];

      yield {
        leverId,
        location: newLocation,
        restrictions,
        type: 'states',
        optionLabels: layer.states.options.map(o => o.label)
      }

      for (let i=0; i<layer.states.options.length; i++) {
        const sublayer = layer.states.options[i];
        for (const lever of indexLayer(sublayer, sublayer.label, newLocation, {...restrictions, [leverId]: i})) {
          yield lever;
        }
      }
    }

    else {
      for (const path of LeverKeys) {
        const value = getPathValue(layer, path);
        if (!isLever(value)) {
          continue;
        }

        const leverId = getLeverId(value);

        yield {
          leverId,
          location: [...location, name],
          restrictions,
          type: path
        }
      }
    }
  }

  const levers = [];
  for (const layer of layout.layers) {
    for (const lever of indexLayer(layer, layer.id, [], {})) {
      levers.push(lever);
    }
  }
  return levers;
}

// resolve a dotted path over an object.
function getPathValue(obj: any, path: string) {
  let current = obj;
  for (const part of path.split('.')) {
    if (typeof current === 'object' && current[part] !== undefined) {
      current = current[part];
    } else {
      return undefined;
    }
  }
  return current;
}